import tokens from '@timelyapp/design-system/tokens.json';
import type { DropdownMenuItem } from '@timelyapp/react';
import { Box, Button, DropdownMenu, Icon, Text } from '@timelyapp/react';
import {
  CURRENCY_SEPARATORS,
  DATATABLE_SETTINGS_LABELS,
  SYMBOL_PLACEMENTS,
  TAG_DISPLAY,
  TAG_STRUCTURE,
  TIME_STYLES,
} from 'components/datatable/constants';
import type {
  CurrencySeparatorOption,
  DatatableSettingsKey,
  DatatableSettingsState,
  SymbolPlacement,
  TagDisplay,
  TagStructure,
} from 'components/datatable/datatable.types';
import ExampleTagsTable from 'components/superreports/ReportExport/ExampleTagsTable';
import { useOnOff } from 'hooks/useOnOff';
import React, { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ColumnModeOptions } from 'redux/modules/accounts.types';
import { getAccountFeature } from 'redux/selectors/accounts';
import type { IState } from 'redux/state';
import { formattedDuration } from 'utils/durations';
import { capitalize } from 'utils/text-helper';
import DatatableColumnOptions from './DatatableColumnOptions';
import styles from './DatatableOptionsDropdown.module.scss';

const tagDisplayToColumnModeOptionsMap: Record<TagDisplay, ColumnModeOptions> = {
  tags: 'tags',
  tagLists: 'parent_tags',
  listsAndTags: 'all',
};

const settingsGroups: Array<{ label: string; keys: Array<DatatableSettingsKey> }> = [
  {
    label: 'Format',
    keys: ['tagStyle', 'timeStyle', 'currencyFormat'],
  },
  {
    label: 'Table style',
    keys: ['zebraStripes', 'verticalBorders'],
  },
] as const;

interface Props {
  columnOrder: Array<string>;
  columnVisibility: Record<string, boolean>;
  settings: DatatableSettingsState;
  onColumnOrderChange: React.Dispatch<React.SetStateAction<Array<string>>>;
  onColumnVisibilityChange: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  onSettingChange: (key: DatatableSettingsKey, value: any) => void;
  resetDatatableState: () => void;
}

const DatatableOptionsDropdown: React.FC<Props> = memo(
  ({
    columnOrder,
    columnVisibility,
    settings,
    onColumnOrderChange,
    onColumnVisibilityChange,
    onSettingChange,
    resetDatatableState,
  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [setOpen, setClosed] = useOnOff(setIsOpen);
    const hasReportTagHeadersFeature = useSelector((state: IState) =>
      getAccountFeature(state, 'report_tag_headers')
    );
    const handleOpenChange = useCallback((open: boolean) => {
      setIsOpen(open);
    }, []);

    const triggerRef = useRef<HTMLButtonElement>(null);
    const [maxHeight, setMaxHeight] = useState(500);

    useLayoutEffect(() => {
      const button = triggerRef.current;
      if (button) {
        const buttonRect = button.getBoundingClientRect();
        const viewportHeight = window.innerHeight;
        const availableSpace = viewportHeight - buttonRect.bottom - buttonRect.height;
        setMaxHeight(availableSpace);
      }
    }, []);

    const getSettingsItem = useCallback(
      (key: DatatableSettingsKey): DropdownMenuItem | undefined => {
        switch (key) {
          case 'zebraStripes':
          case 'verticalBorders':
            return {
              type: 'checkboxItem',
              checked: settings[key]!,
              label: DATATABLE_SETTINGS_LABELS[key],
              onCheckedChange: (checked: boolean) => {
                onSettingChange(key, checked);
              },
            };
          case 'timeStyle':
            return {
              type: 'subMenu',
              icon: <Icon name="ClockPlus" />,
              label: DATATABLE_SETTINGS_LABELS[key],
              subOptions: [
                {
                  type: 'radioGroup',
                  label: '',
                  currentValue: settings[key]!,
                  onValueChange: value => {
                    onSettingChange(key, value);
                  },
                  subOptions: TIME_STYLES.map(timeStyle => ({
                    type: 'radioItem',
                    value: timeStyle,
                    label: capitalize(timeStyle),
                    sidenote: formattedDuration(135, timeStyle),
                  })),
                },
              ],
            };
          case 'currencyFormat':
            return {
              type: 'subMenu',
              icon: <Icon name="CurrencyDollarCircle" />,
              label: DATATABLE_SETTINGS_LABELS[key],
              subOptions: [
                {
                  type: 'radioGroup',
                  label: 'Thousands separator – decimal separator',
                  currentValue: settings[key]!.separator as CurrencySeparatorOption,
                  onValueChange: value => {
                    onSettingChange(key, { ...settings[key], separator: value });
                  },
                  subOptions: CURRENCY_SEPARATORS.map(separators => ({
                    type: 'radioItem',
                    value: separators,
                    label: formatSeparatorsLabel(separators),
                  })),
                },
                {
                  type: 'separator',
                },
                {
                  type: 'radioGroup',
                  label: 'Currency symbol placement',
                  currentValue: settings[key]!.symbolPlacement as SymbolPlacement,
                  onValueChange: value => {
                    onSettingChange(key, { ...settings[key], symbolPlacement: value });
                  },
                  subOptions: SYMBOL_PLACEMENTS.map(placement => ({
                    type: 'radioItem',
                    value: placement.value,
                    label: placement.label,
                  })),
                },
              ],
            };
          case 'tagStyle':
            return {
              type: 'subMenu',
              icon: <Icon name="Tag01" />,
              label: DATATABLE_SETTINGS_LABELS[key],
              subOptions: [
                ...(hasReportTagHeadersFeature
                  ? ([
                      {
                        type: 'radioGroup',
                        label: 'Tag structure',
                        currentValue: settings[key]!.tagStructure as TagStructure,
                        onValueChange: (value: string | number | boolean) => {
                          onSettingChange(key, { ...settings[key], tagStructure: value });
                        },
                        subOptions: TAG_STRUCTURE.map(item => ({
                          type: 'radioItem',
                          value: item.value,
                          label: item.label,
                        })),
                      },
                    ] as Array<DropdownMenuItem>)
                  : []),
                {
                  type: 'radioGroup',
                  label: 'Show',
                  disabled: settings.tagStyle?.tagStructure === 'separate',
                  currentValue: settings[key]!.tagDisplay as TagDisplay,
                  onValueChange: value => {
                    onSettingChange(key, { ...settings[key], tagDisplay: value });
                  },
                  subOptions: TAG_DISPLAY.map(display => ({
                    type: 'radioItem',
                    value: display.value,
                    label: display.label,
                  })),
                },
                {
                  type: 'custom' as const,
                  content: (
                    <div className={styles.exampleWrapper}>
                      <Box.Column className={styles.inner} gap="medium">
                        <Text size="xsmall" color="subtle" style={{ alignSelf: 'flex-end' }}>
                          Example
                        </Text>
                        <ExampleTagsTable
                          tagsMode={tagDisplayToColumnModeOptionsMap[settings[key]!.tagDisplay]}
                          tagsStructure={settings[key]!.tagStructure as TagStructure}
                        />
                      </Box.Column>
                    </div>
                  ),
                },
              ],
            };
        }
      },
      [hasReportTagHeadersFeature, onSettingChange, settings]
    );

    const columnVisibilityMap = useMemo(() => {
      const realColumnVisibility = { ...columnVisibility };
      if (settings.tagStyle?.tagStructure === 'separate') {
        realColumnVisibility['tags'] = true;
      }
      return realColumnVisibility;
    }, [columnVisibility, settings.tagStyle?.tagStructure]);

    const settingsItems = useMemo(() => {
      const items: Array<DropdownMenuItem> = [];
      settingsGroups.forEach(group => {
        if (group.keys.some(key => settings[key] !== undefined)) {
          items.push({ type: 'separator' });
          items.push({ type: 'label', label: group.label });
          const groupItems = group.keys.map(getSettingsItem).filter(i => i !== undefined);
          items.push(...groupItems);
        }
      });
      return items;
    }, [getSettingsItem, settings]);

    const items = useMemo<Array<DropdownMenuItem>>(() => {
      return [
        {
          type: 'custom',
          content: (
            <Box.Row
              flex={1}
              style={{
                minHeight: `calc(${columnOrder.length} * ${tokens['theme_click-area_small']})`,
              }}
            >
              <DatatableColumnOptions
                columnOrder={columnOrder}
                columnVisibility={columnVisibilityMap}
                onColumnOrderChange={onColumnOrderChange}
                onColumnVisibilityChange={onColumnVisibilityChange}
              />
            </Box.Row>
          ),
        },
        ...settingsItems,
        {
          type: 'custom',
          content: (
            <Box.Row flex={1}>
              <DropdownActions resetDatatableState={resetDatatableState} setClosed={setClosed} />
            </Box.Row>
          ),
        },
      ];
    }, [
      columnOrder,
      columnVisibilityMap,
      onColumnOrderChange,
      onColumnVisibilityChange,
      settingsItems,
      resetDatatableState,
      setClosed,
    ]);

    return (
      <DropdownMenu
        open={isOpen}
        onOpenChange={handleOpenChange}
        trigger={
          <Button
            ref={triggerRef}
            onClick={setOpen}
            priority="low"
            size="small"
            data-testid="timesheets_datatable_options"
          >
            <Icon name="Settings02" type="solid" /> Options
          </Button>
        }
        items={items}
        side="bottom"
        style={{ maxHeight: `${maxHeight}px` }}
      />
    );
  }
);

interface DropdownActionsProps {
  resetDatatableState: () => void;
  setClosed: () => void;
}

const DropdownActions: React.FC<DropdownActionsProps> = memo(
  ({ resetDatatableState, setClosed }) => {
    return (
      <Box.Row className={styles.actions} gap="medium" justifyContent="right" flex={1}>
        <Button onClick={resetDatatableState} priority="xlow" size="small">
          Reset
        </Button>
        <Button onClick={setClosed} priority="low" size="small">
          Done
        </Button>
      </Box.Row>
    );
  }
);
DropdownActions.displayName = 'DropdownActions';

DatatableOptionsDropdown.displayName = 'DatatableOptionsDropdown';

function formatSeparatorsLabel(separators: CurrencySeparatorOption) {
  const parts = separators.split('-');
  return `${capitalize(parts[0])} - ${capitalize(parts[1])}`;
}

export default DatatableOptionsDropdown;
